import { Injectable } from '@angular/core';
import { Fee } from '../../shared/data-objects/fee';
import { ServiceBase } from './service-base';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../app-environments/environment';
import { AddressService } from './address.service';

export const enum SERVICE_PROVIDER {
  CWW = 'FMR CWW',
  WW = 'FMR WW'
}

@Injectable()
export class FeeService extends ServiceBase {

  private _fees: Fee[];

  public static get feeUrl(): string {
    return ServiceBase.baseHref + '/api/fees';
  }

  constructor(private http: HttpClient, private addressService: AddressService) {
    super();
  }

  retrieveFees(applicationType, token: string, okCb: (boolean) => void, errorCb?: (any) => void): void {
    // Default pricing zone to "FMR CWW" if pricing zone does not exist
    this.addressService.getPricingZone().subscribe(_pricingZone => {
      const params: HttpParams = new HttpParams()
        .set('applicationType', applicationType)
        .set('pricingZone', _pricingZone ? (_pricingZone || '').toUpperCase() : SERVICE_PROVIDER.CWW);

      const header: HttpHeaders = new HttpHeaders()
        .append('Content-Type', 'application/json')
        .append('Authorization', 'Bearer ' + token);
      this.http.get<Fee[]>(FeeService.feeUrl, { headers: header, params: params })
        .subscribe(data => {
          this._fees = data;
          okCb(true);
        }, err => {
          ServiceBase.handleError(err, errorCb);
        });
    }, err => {
      ServiceBase.handleError(err, errorCb);
    });
  }

  getASSPFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodesASSP);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodesASSP :     // cww fee
      environment.feeCodesASSP_WW);  // ww fee
  }

  getSEWDFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodesSEWD);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodesSEWD :     // cww fee
      environment.feeCodesSEWD_WW);  // ww fee
  }

  private getFeeBasedOnCode(feeCode: string): number {
    if (!this._fees || this._fees.length === 0) {
      return 0;
    }

    for (let i = 0; i < this._fees.length; i++) {
      if (this._fees[i].code === feeCode) {
        return this._fees[i].amount;
      }
    }
    return 0;
  }

  private getFee(originalCWWFeeCode: string): number {
    return this.getFeeBasedOnCode(originalCWWFeeCode);
  }

  getSRPApplicationFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodePA);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodePA :     // cww fee
      environment.feeCodePA_WW);  // ww fee
  }

  getCHMApplicationFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodePA_C);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodePA_C :     // cww fee
      environment.feeCodePA_C_WW);  // ww fee
  }

  getNextWorkingDayFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodePFI);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodePFI :     // cww fee
      environment.feeCodePFI_WW);  // ww fee
  }

  getWithin1To10LotsAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodeAPPF0);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodeAPPF0 :     // cww fee
      environment.feeCodeAPPF0_WW);  // ww fee
  }

  getapplicationFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodeOPP_2);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodeOPP_2 :     // cww fee
      environment.feeCodeOPP_2_WW);  // ww fee
  }

  getGreaterThan10LotsAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodeAPPF10_);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodeAPPF10_ :     // cww fee
      environment.feeCodeAPPF10__WW);  // ww fee
  }

  getWithin5BusinessDayFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodePF);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodePF :     // cww fee
      environment.feeCodePF_WW);  // ww fee
  }

  getBOEFee(pricingZone: string = SERVICE_PROVIDER.CWW) {
    // return this.getFeeCode(environment.feeCodeEAS);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodeEAS :     // cww fee
      environment.feeCodeEAS_WW);  // ww fee
  }

  getWaterMeterFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW) {
    // return this.getFeeCode(environment.feeCodeCF202);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodeCF202 :     // cww fee
      environment.feeCodeCF202_WW);  // ww fee
  }

  getRemoteWaterMeterFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW) {
    // return this.getFeeCode(environment.feeCodeRMDP);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodeRMDP :     // cww fee
      environment.feeCodeRMDP_WW);  // ww fee
  }

  getCMPApplicationFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW, fireServiceSelected: boolean) {

    let feeCode: string = null;

    if (pricingZone === SERVICE_PROVIDER.CWW) {
      feeCode = fireServiceSelected ? environment.feeCodePAC_F : environment.feeCodePAC_NF;
    }
    
    if (pricingZone === SERVICE_PROVIDER.WW) {
      feeCode = fireServiceSelected ? environment.feeCodePAC_WW_F : environment.feeCodePAC_WW_NF;
    }
    
    console.log(feeCode);
    return this.getFee(feeCode);
  }

  getHydrantApplicationFeeAmount(pricingZone: string = SERVICE_PROVIDER.CWW): number {
    // return this.getFeeCode(environment.feeCodeHPF);
    return this.getFee(pricingZone === SERVICE_PROVIDER.CWW ?
      environment.feeCodeHPF :     // cww fee
      environment.feeCodeHPF_WW);  // ww fee
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/fee.service.ts